import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setDocuments, setMoreDocuments } from '../actions';
export let maxItemPerBatch = 10;

export function useInfiniteScroll (props) {
  const { path, getDocuments, getBundles, getSignerDocuments, filterByWaitingForMe } = props;
  const [noMoreData, setNoMoreData] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [nextToken, setNextToken] = useState(null);
  const [initialLoad, setInitialLoad] = useState(false);
  const [abortController, setAbortController] = useState(null);

  const dispatch = useDispatch();
  const documents = useSelector(state => state.documents.documents);
  const filter = useSelector(state => state.filter);
  let searchByDocument = props.searchByDocument || false;

  const getRouteProperties = (path) => {
    const routeMappings = {
      '/notification': {
        getItems: getDocuments,
        itemType: 'document',
        resultType: 'listDocuments'
      },
      '/document': {
        getItems: getDocuments,
        itemType: 'document',
        resultType: 'listDocuments'
      },
      '/document/finished': {
        getItems: getDocuments,
        itemType: 'document',
        resultType: 'listDocuments'
      },
      '/bundle': {
        getItems: getBundles,
        itemType: 'bundle',
        resultType: 'listBundles'
      },
      '/file': {
        getItems: getDocuments,
        itemType: 'file',
        resultType: 'listDocuments'
      },
      '/signer/document': {
        getItems: getSignerDocuments,
        itemType: 'document',
        resultType: 'listDocumentsByEmail'
      }
    };

    const route = routeMappings[path];
    if (route) {
      return {
        getItems: route.getItems,
        itemType: route.itemType,
        resultType: route.resultType
      };
    }
    return {};
  };

  const { getItems, itemType } = getRouteProperties(path);

  async function getListDocuments () {
    if (abortController) {
      abortController.abort();
    }
    // eslint-disable-next-line no-undef
    const newAbortController = new AbortController();
    setAbortController(newAbortController);
    try {
      setLoadingData(true);
      const [result, nextToken] = await getItems({ searchByDocument, filterByWaitingForMe, abortSignal: newAbortController.signal });

      const resultType = getRouteProperties(path).resultType;

      const items = result.data[resultType];
      const newNextToken = nextToken || null;

      dispatch(setDocuments({ items: items.items, nextToken: newNextToken }));
      setNextToken(newNextToken);

      if (!newNextToken) {
        setNoMoreData(true);
      }
      setInitialLoad(true);
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted');
      } else {
        console.error('Error occurred while fetching documents', error);
      }
    } finally {
      setLoadingData(false);
    }
  }
  useEffect(() => {
    return () => {
      if (abortController) {
        abortController.abort();
      }
    };
  }, [abortController]);

  const clearStateOnChangeTab = () => {
    dispatch(setDocuments({ items: [], nextToken: null }));
    setNextToken(null);
  };

  useEffect(() => {
    setNoMoreData(false);
    if (filter && initialLoad) {
      getListDocuments();
    }
  }, [filter, filterByWaitingForMe]);

  useEffect(() => {
    if (documents.length > 0) {
      clearStateOnChangeTab();
    }
    if (!initialLoad) {
      getListDocuments();
    }
  }, []);

  const loadNextBatchItems = async () => {
    try {
      setLoadingData(true);
      const [result, newNextToken] = await getItems({
        currentPath: path,
        filter,
        itemType,
        nextToken,
        searchByDocument,
        filterByWaitingForMe
      });

      const resultType = getRouteProperties(path).resultType;
      const items = result.data[resultType];

      dispatch(setMoreDocuments({ items: items.items, nextToken: newNextToken }));
      setNextToken(newNextToken);

      if (!newNextToken) {
        setNoMoreData(true);
      }
    } catch (error) {
      console.error('Error occurred while fetching more documents', error);
    } finally {
      setLoadingData(false);
    }
  };

  return [loadingData, noMoreData, loadNextBatchItems];
}
